import React, { useMemo, useState } from "react";
import {
  Stack,
  Group,
  TextInput,
  Select,
  Button,
  Text,
  Paper,
  ActionIcon,
  Grid,
  Alert,
} from "@mantine/core";
import { IconPlus, IconTrash, IconInfoCircle, IconShieldCheck } from "@tabler/icons";
import McaWatchFieldExplanationModal from "./McaWatchFieldExplanationModal";
import McaConditionValueInput from "./McaConditionValueInput";
import {
  buildWatchFieldSelectOptions,
  getWatchFieldSelectOption,
} from "./mcaWatchFieldLabels";
import {
  emptyWatchFieldRow,
  catchAllApprovalTitle,
  hasWatchFieldConditions,
} from "../../helpers/mcaWatchFieldsHelpers";
import {
  getMcaWatchFieldMeta,
  getMcaConditionTypesForField,
  isMcaConditionTypeAllowed,
} from "../../helpers/mcaWatchFieldMeta";

export { watchFieldsToRows, rowsToWatchFields, hasWatchFieldConditions } from "../../helpers/mcaWatchFieldsHelpers";

const emptyRow = emptyWatchFieldRow;

const WATCH_FIELD_SELECT_STYLES = {
  dropdown: {
    minWidth: "max-content",
    width: "max-content",
    maxWidth: "min(90vw, 560px)",
  },
  item: {
    whiteSpace: "nowrap",
  },
};

const McaWatchFieldsEditor = ({
  rows,
  onChange,
  suggestedFields = [],
  entityKey = null,
  action = "UPDATE",
}) => {
  const [explainOpen, setExplainOpen] = useState(false);
  const fieldPaths = useMemo(
    () => (Array.isArray(suggestedFields) ? suggestedFields.filter(Boolean) : []),
    [suggestedFields]
  );
  const fieldOptions = useMemo(
    () => buildWatchFieldSelectOptions(entityKey, fieldPaths),
    [entityKey, fieldPaths]
  );

  const hasActiveConditions = hasWatchFieldConditions(rows);
  const showConditionRows = rows.length > 0;
  const conditionalHelpText =
    action === "CREATE"
      ? "Add a condition below to require approval only when the entity payload matches (re-evaluated on Submit for approval)."
      : action === "DELETE"
        ? "Add a condition below to require approval only when conditions match."
        : "Add a condition below to require approval only when specific fields change.";

  const updateRow = (idx, patch) => {
    const next = rows.map((r, i) => (i === idx ? { ...r, ...patch } : r));
    if (!hasWatchFieldConditions(next)) {
      const allBlank = next.every(
        (row) => !row.field?.trim() && !row.conditionType && !row.conditionValue
      );
      if (allBlank) {
        onChange([]);
        return;
      }
    }
    onChange(next);
  };

  const handleFieldChange = (idx, fieldPath) => {
    updateRow(idx, { field: fieldPath ?? "", conditionType: "", conditionValue: "" });
  };

  const handleConditionChange = (idx, row, conditionType) => {
    const meta = getMcaWatchFieldMeta(entityKey, row.field);
    const nextType = conditionType ?? "";
    if (nextType && !isMcaConditionTypeAllowed(meta, nextType)) {
      return;
    }
    updateRow(idx, { conditionType: nextType, conditionValue: "" });
  };

  const removeRow = (idx) => {
    const next = rows.filter((_, i) => i !== idx);
    onChange(hasWatchFieldConditions(next) ? next : []);
  };

  const addRow = () => {
    onChange([...rows, emptyRow()]);
  };

  return (
    <Stack>
      {!hasActiveConditions ? (
        <Alert
          icon={<IconShieldCheck size={18} />}
          color="teal"
          title={catchAllApprovalTitle(action)}
        >
          This process will always trigger for the selected action. Use &quot;Add condition&quot;
          below only if approval should depend on specific field values.
        </Alert>
      ) : (
        <Text size="sm" color="dimmed">
          {conditionalHelpText}
        </Text>
      )}
      {showConditionRows &&
        rows.map((row, idx) => {
          const meta = getMcaWatchFieldMeta(entityKey, row.field);
          const conditionTypes = getMcaConditionTypesForField(meta);
          return (
        <Paper key={idx} p="md" withBorder>
          <Grid align="flex-end" gutter="sm">
            <Grid.Col span={12} md={5}>
            {fieldOptions.length > 0 ? (
              <Select
                label="Field"
                placeholder="Select field"
                data={[
                  ...fieldOptions,
                  ...(row.field &&
                  !fieldOptions.some((option) => option.value === row.field)
                    ? [getWatchFieldSelectOption(entityKey, row.field)].filter(Boolean)
                    : []),
                ]}
                value={row.field || null}
                onChange={(v) => handleFieldChange(idx, v)}
                searchable
                clearable
                withinPortal
                styles={{
                  ...WATCH_FIELD_SELECT_STYLES,
                  input: { whiteSpace: "nowrap" },
                }}
              />
            ) : (
              <TextInput
                label="Field"
                placeholder="e.g. status"
                value={row.field}
                onChange={(e) => handleFieldChange(idx, e.target.value)}
              />
            )}
            </Grid.Col>
            <Grid.Col span={6} md={2}>
            <Select
              label="Condition"
              data={conditionTypes}
              value={row.conditionType || null}
              onChange={(v) => handleConditionChange(idx, row, v)}
              clearable
              placeholder="Any change"
            />
            </Grid.Col>
            <Grid.Col span={10} md={4}>
            <McaConditionValueInput
              entityKey={entityKey}
              field={row.field}
              conditionType={row.conditionType}
              value={row.conditionValue}
              onChange={(nextValue) => updateRow(idx, { conditionValue: nextValue })}
              disabled={!row.conditionType}
            />
            </Grid.Col>
            <Grid.Col span={2} md={1}>
            <Group position="right" pb={4}>
              <ActionIcon
                variant="subtle"
                size="lg"
                onClick={() => removeRow(idx)}
              >
                <IconTrash size={16} />
              </ActionIcon>
            </Group>
            </Grid.Col>
          </Grid>
        </Paper>
          );
        })}
      <Group>
        <Button leftIcon={<IconPlus size={16} />} onClick={addRow}>
          {hasActiveConditions ? "Add condition" : "Add condition (optional)"}
        </Button>
        {entityKey && fieldPaths.length > 0 && (
          <Button
            variant="default"
            leftIcon={<IconInfoCircle size={16} />}
            onClick={() => setExplainOpen(true)}
          >
            Field guide
          </Button>
        )}
      </Group>
      <McaWatchFieldExplanationModal
        opened={explainOpen}
        onClose={() => setExplainOpen(false)}
        entityKey={entityKey}
        fieldPaths={fieldPaths}
      />
    </Stack>
  );
};

export default McaWatchFieldsEditor;
